import { IBooking } from '../interfaces/heavyDutyMachinery/IBooking';
import { TRANSLATIONS } from '../constants/translations';

/**
 * Where a booking's machine physically is, and what to say when we can't
 * name it.
 *
 * This is the front half of BookingAddressPolicy (backend). Three screens
 * show a booking's location -- the chat's details panel, its "ver dirección"
 * popup, and the tracking page -- and each of them had grown its own copy of
 * `booking.status === 'Confirmed' && booking.privateAddress`, with the same
 * italic apology underneath when that was false. That test was inherited
 * from a platform where the customer always travelled to the thing they
 * booked, and it was wrong here in three ways at once:
 *
 * - it showed the HOST the message "the exact address is shown once the
 *   booking is confirmed" about the host's own yard;
 * - it went back to hiding the address at Completed, while the machine is
 *   often still on site;
 * - it only ever described ONE address, when half of these bookings are
 *   delivered and the address that matters then is the guest's own worksite
 *   (deliveryLatitude/deliveryLongitude/deliveryInstructions), which no
 *   screen showed at all.
 *
 * So the reveal decision is NOT taken here: the server already made it, and
 * `booking.privateAddress` is simply absent when this viewer isn't entitled
 * to it. What this helper does is say what to render in either case, once,
 * for every screen -- including which of the two addresses a delivered
 * booking is actually about.
 */
export interface BookingLocationView {
  /** True when the machine travels to the guest's site instead of being collected. */
  delivered: boolean;
  /** True when the host also collects it at the end (a second trip, see returnPickupRequested). */
  collectedBack: boolean;
  /** Label for the yard the machine leaves from / is returned to. */
  pickupLabelKey: string;
  /** The yard's exact address, or undefined when this viewer hasn't been told it. */
  pickupAddress?: string;
  /**
   * Why there is no address -- only set when pickupAddress is undefined, so
   * a screen never has to guess between "not yet" and "never set".
   */
  pickupMissingKey?: string;
  /** Label for the guest's own worksite. Only meaningful when delivered. */
  deliveryLabelKey: string;
  /** The worksite, as coordinates -- there is no street string for it anywhere in the system. */
  deliveryPoint?: { latitude: number; longitude: number };
  /** What the guest typed about the site ("entrar marcha atrás..."), when they typed anything. */
  deliveryInstructions?: string;
}

/**
 * @param isHost Whoever is looking. Only used to pick the wording -- a host
 * reading "you'll see the address once payment clears" about their own
 * machine is the exact bug this replaces. It is deliberately NOT used to
 * decide what to reveal: that is the server's call and a client-side
 * `isHost` would be a suggestion, not a gate.
 */
export const buildBookingLocationView = (booking: IBooking, isHost: boolean): BookingLocationView => {
  const delivered = !!booking.deliveryRequested;

  return {
    delivered,
    collectedBack: !!booking.returnPickupRequested,
    pickupLabelKey: delivered
      ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LOCATION_ORIGIN_LABEL
      : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LOCATION_PICKUP_LABEL,
    pickupAddress: booking.privateAddress || undefined,
    pickupMissingKey: booking.privateAddress
      ? undefined
      : isHost
        ? // The host is the only person who can fix this one, so they get the
          // version that says what to do rather than the one that asks them
          // to wait for themselves.
          TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LOCATION_ADDRESS_NOT_SET
        : TRANSLATIONS.HEAVY_DUTY_MACHINERY_MESSAGES_ADDRESS_NOT_AVAILABLE,
    deliveryLabelKey: TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_LOCATION_DELIVERY_LABEL,
    deliveryPoint:
      delivered && booking.deliveryLatitude != null && booking.deliveryLongitude != null
        ? { latitude: booking.deliveryLatitude, longitude: booking.deliveryLongitude }
        : undefined,
    deliveryInstructions: delivered ? booking.deliveryInstructions || undefined : undefined,
  };
};

/**
 * How the machine changes hands, as one short phrase -- the row that
 * replaced "Modalidad: Overnight" in the details panel.
 *
 * Its own export because the same three-way answer is worth exactly the
 * same words on the tracking page and in the delivery note, and because
 * "entrega y recogida" vs "entrega" is a real difference in what the host
 * has committed to (two trips, not one) that a caller should not have to
 * remember to spell out.
 */
export const handoverModeKey = (booking: IBooking): string => {
  if (!booking.deliveryRequested) return TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_HANDOVER_SELF_PICKUP;
  return booking.returnPickupRequested
    ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_HANDOVER_DELIVERY_AND_COLLECTION
    : TRANSLATIONS.HEAVY_DUTY_MACHINERY_BOOKING_HANDOVER_DELIVERY;
};
